import React from 'react'
import Image from 'next/image'

/** Splits title into lines of up to `wordsPerLine` words (mobile-friendly). */
function titleWithLineBreaks(text: string, wordsPerLine: number = 3): React.ReactNode {
  const words = text.split(/\s+/).filter(Boolean)
  return words.map((word, i) => (
    <React.Fragment key={i}>
      {i > 0 && i % wordsPerLine === 0 ? <br /> : i > 0 ? ' ' : null}
      {word}
    </React.Fragment>
  ))
}

export interface TimelineEntryProps {
  year: string
  title: string | React.ReactNode
  description: React.ReactNode
  /** Container positioning: e.g. "absolute left-[20%]" */
  containerClassName: string
  /** Baseline position as % from bottom (red curve); e.g. "35%". Ignored when bottomClassName is set. */
  baselineFromBottom?: string
  /** Optional responsive bottom positioning, e.g. "bottom-[45%] min-[1920px]:bottom-[48%]" for large-screen alignment */
  bottomClassName?: string
  /** Whether title should not wrap (whitespace-nowrap) */
  titleNowrap?: boolean
  /** Text block extra classes for position */
  textBlockClassName?: string
  /** Optional extra classes for the timeline marker icon */
  iconClassName?: string
  /** Optional extra Y offset for the icon (e.g. '0.75rem' to move icon down) */
  iconTranslateY?: string
  /** Optional extra X offset for the icon (e.g. '-0.75rem' to move icon left) */
  iconTranslateX?: string
  /** Optional extra Y offset for the content block (e.g. '1rem' to move title/description down) */
  contentTranslateY?: string
  /** Optional extra X offset for the content block (e.g. '-1rem' to move title/description left) */
  contentTranslateX?: string
  /** Optional extra Y offset for the year label only (e.g. '-1rem' to move year up) */
  yearTranslateY?: string
}

const ICON_SIZE = 'clamp(1.5rem,2.5vw,3rem)'
/** Year on red curve (baseline aligned with icon) */
const BELOW_LINE_OFFSET_YEAR = 'calc(-1.25rem - 0.5rem)'
const YEAR_TRANSLATE_UP = '0' /* year on the curve */
const BELOW_LINE_OFFSET_CONTENT =
  'calc(-1.25rem - 4.5rem)' /* content (title + description) below curve */

export function TimelineEntry({
  year,
  title,
  description,
  containerClassName,
  baselineFromBottom,
  bottomClassName,
  titleNowrap = false,
  textBlockClassName = '',
  iconClassName = '',
  iconTranslateY,
  iconTranslateX,
  contentTranslateY,
  contentTranslateX,
  yearTranslateY,
}: TimelineEntryProps) {
  /* Reference: year below and left of icon; content below and right of icon; icon on red line. */
  const style =
    bottomClassName == null && baselineFromBottom != null
      ? { bottom: baselineFromBottom }
      : undefined
  return (
    <div
      className={`z-10 flex min-w-0 max-w-[90vw] min-[1920px]:max-w-[520px] flex-row items-end gap-[0.4em] min-[1920px]:gap-2 ${containerClassName} ${bottomClassName ?? ''}`}
      style={style}
    >
      {/* Year: above red line, left of icon; no overlap with content below */}
      <span
        className="font-bebas-neue inline-block shrink-0 select-none text-[clamp(2rem,5.2vw,6rem)] leading-none tracking-tight text-[#1a1a1a] opacity-[0.12] ml-5 min-[1920px]:ml-8"
        style={{
          marginBottom: BELOW_LINE_OFFSET_YEAR,
          marginLeft: '5rem',
          marginTop: '3rem',
          transform: yearTranslateY
            ? `translateY(calc(${YEAR_TRANSLATE_UP} + ${yearTranslateY}))`
            : `translateY(${YEAR_TRANSLATE_UP})`,
        }}
        aria-hidden
      >
        {year}
      </span>
      {/* Icon: centered exactly on the red line */}
      <div
        className={`z-10 shrink-0 drop-shadow-[0_2px_6px_rgba(0,0,0,0.1)] ${iconClassName}`}
        style={{
          width: ICON_SIZE,
          height: ICON_SIZE,
          transform:
            iconTranslateX || iconTranslateY
              ? `translate(${iconTranslateX ?? '0'}, ${iconTranslateY ? `calc(50% + ${iconTranslateY})` : '50%'})`
              : 'translateY(50%)',
        }}
        aria-hidden
      >
        <Image
          src="/icon/icontimeline.png"
          alt="Timeline"
          width={48}
          height={48}
          className="h-full w-full object-contain"
        />
      </div>
      {/* Content: entirely below red line, right of icon (top at icon bottom) */}
      <div
        className={`relative z-10 min-w-0 max-w-[14.6vw] min-[1920px]:max-w-[280px] pl-[0.2em] -ml-2 min-[1920px]:-ml-3 ${textBlockClassName}`}
        style={{
          marginBottom: BELOW_LINE_OFFSET_CONTENT,
          ...((contentTranslateX || contentTranslateY) && {
            transform:
              contentTranslateX && contentTranslateY
                ? `translate(${contentTranslateX}, ${contentTranslateY})`
                : contentTranslateX
                  ? `translateX(${contentTranslateX})`
                  : `translateY(${contentTranslateY})`,
          }),
        }}
      >
        <h3
          className={`font-bebas-neue text-left text-[0.5rem] font-bold uppercase leading-none tracking-tight text-[#1a1a1a] md:leading-normal md:text-[clamp(0.65rem,1.4vw,1.25rem)] ${titleNowrap ? 'md:whitespace-nowrap' : ''}`}
        >
          <span className="md:hidden">
            {typeof title === 'string' ? titleWithLineBreaks(title, 3) : title}
          </span>
          <span className="hidden md:inline">{title}</span>
        </h3>
        <p className="font-poppins mt-[0.15em] text-left text-[0.4rem] leading-relaxed text-neutral-600 md:text-[clamp(0.55rem,1vw,1rem)]">
          {description}
        </p>
      </div>
    </div>
  )
}
